import React, { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  Database,
  FileX,
  RefreshCw,
  CheckCircle,
  Info,
} from 'lucide-react';
import { SkuValidationResult } from '../hooks/useSkuValidation';
import { cn } from '@/lib/utils';

interface SkuValidationWarningProps {
  validationResult: SkuValidationResult;
  onRefresh?: () => void;
  showDetails?: boolean;
  className?: string;
}

export function SkuValidationWarning({
  validationResult,
  onRefresh,
  showDetails = true,
  className,
}: SkuValidationWarningProps) {
  const [showInFileDetails, setShowInFileDetails] = useState(false);
  const [showExistingDetails, setShowExistingDetails] = useState(false);

  // Don't show anything if validation is still loading or there's no SKU column
  if (validationResult.isValidating || !validationResult.hasSkuColumn) {
    return null;
  }

  // Show error if validation failed
  if (validationResult.validationError) {
    return (
      <Alert className={cn('border-red-200 bg-red-50', className)}>
        <AlertTriangle className="size-4 text-red-600" />
        <AlertTitle className="font-medium text-red-800">SKU Validation Error</AlertTitle>
        <AlertDescription className="text-red-700">
          <div className="space-y-2">
            <p>{validationResult.validationError}</p>
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                <RefreshCw className="mr-1 size-3" />
                Retry
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  const hasInFileIssues = validationResult.inFileDuplicates.length > 0;
  const hasExistingIssues = validationResult.existingDuplicates.length > 0;
  const shouldWarnOnExisting = validationResult.shouldWarnOnExisting;

  // Show success message if no issues
  if (!hasInFileIssues && !hasExistingIssues) {
    return (
      <Alert className={cn('border-green-200 bg-green-50', className)}>
        <CheckCircle className="size-4 text-green-600" />
        <AlertTitle className="font-medium text-green-800">SKU Validation Passed</AlertTitle>
        <AlertDescription className="text-green-700">
          <div className="flex items-center justify-between">
            <div>All {validationResult.stats.total} SKUs are valid and ready for import.</div>
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="border-green-300 text-green-700 hover:bg-green-100"
              >
                <RefreshCw className="mr-1 size-3" />
                Recheck
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Determine alert type based on severity
  const isWarningLevel = hasInFileIssues || (hasExistingIssues && shouldWarnOnExisting);
  const isInfoLevel = hasExistingIssues && !shouldWarnOnExisting && !hasInFileIssues;

  const alertClasses = isWarningLevel
    ? 'border-amber-200 bg-amber-50'
    : 'border-blue-200 bg-blue-50';

  const iconClasses = isWarningLevel ? 'text-amber-600' : 'text-blue-600';

  const titleClasses = isWarningLevel ? 'text-amber-800' : 'text-blue-800';

  const textClasses = isWarningLevel ? 'text-amber-700' : 'text-blue-700';

  const IconComponent = isWarningLevel ? AlertTriangle : Info;

  return (
    <Alert className={cn(alertClasses, className)}>
      <IconComponent className={cn('h-4 w-4', iconClasses)} />
      <AlertTitle className={cn('font-medium', titleClasses)}>
        {isWarningLevel ? 'SKU Validation Warnings' : 'SKU Information'}
      </AlertTitle>
      <AlertDescription className={textClasses}>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              {isWarningLevel
                ? 'Issues found that may prevent successful import.'
                : 'Information about your SKUs and import strategy.'}
            </div>
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className={cn(
                  isWarningLevel
                    ? 'border-amber-300 text-amber-700 hover:bg-amber-100'
                    : 'border-blue-300 text-blue-700 hover:bg-blue-100'
                )}
              >
                <RefreshCw className="mr-1 size-3" />
                Recheck
              </Button>
            )}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Total SKUs:</span> {validationResult.stats.total}
            </div>
            <div>
              <span className="font-medium">Distinct SKUs:</span> {validationResult.stats.unique}
              <span className="ml-1 text-xs text-gray-500">(in file)</span>
            </div>
          </div>

          {/* In-file duplicates (always a warning) */}
          {hasInFileIssues && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileX className="size-4 text-red-600" />
                    <span className="font-medium text-red-800">
                      In-File Duplicates ({validationResult.inFileDuplicates.length})
                    </span>
                    <Badge variant="destructive" className="text-xs">
                      Warning
                    </Badge>
                  </div>
                  {showDetails && (
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowInFileDetails(!showInFileDetails)}
                          className="h-auto p-1 text-red-700 hover:bg-red-100"
                        >
                          {showInFileDetails ? (
                            <ChevronDown className="size-3" />
                          ) : (
                            <ChevronRight className="size-3" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {showInFileDetails && (
                          <div className="mt-2 space-y-1">
                            <p className="text-sm text-red-700">
                              These SKUs appear multiple times in your file and will cause import
                              failures:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {validationResult.inFileDuplicates.map((sku, index) => (
                                <Badge key={index} variant="destructive" className="text-xs">
                                  {sku}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Existing duplicates (warning level depends on strategy) */}
          {hasExistingIssues && (
            <Card
              className={cn(
                shouldWarnOnExisting ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'
              )}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Database
                      className={cn(
                        'h-4 w-4',
                        shouldWarnOnExisting ? 'text-amber-600' : 'text-blue-600'
                      )}
                    />
                    <span
                      className={cn(
                        'font-medium',
                        shouldWarnOnExisting ? 'text-amber-800' : 'text-blue-800'
                      )}
                    >
                      Existing SKUs ({validationResult.existingDuplicates.length})
                    </span>
                    <Badge
                      variant={shouldWarnOnExisting ? 'destructive' : 'secondary'}
                      className="text-xs"
                    >
                      {shouldWarnOnExisting ? 'Warning' : 'Info'}
                    </Badge>
                  </div>
                  {showDetails && (
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowExistingDetails(!showExistingDetails)}
                          className={cn(
                            'p-1 h-auto',
                            shouldWarnOnExisting
                              ? 'text-amber-700 hover:bg-amber-100'
                              : 'text-blue-700 hover:bg-blue-100'
                          )}
                        >
                          {showExistingDetails ? (
                            <ChevronDown className="size-3" />
                          ) : (
                            <ChevronRight className="size-3" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {showExistingDetails && (
                          <div className="mt-2 space-y-1">
                            <p
                              className={cn(
                                'text-sm',
                                shouldWarnOnExisting ? 'text-amber-700' : 'text-blue-700'
                              )}
                            >
                              {shouldWarnOnExisting
                                ? 'These SKUs already exist and will cause import failures in abort mode:'
                                : 'These SKUs already exist in your database and will be handled according to your import strategy:'}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {validationResult.existingDuplicates.map((sku, index) => (
                                <Badge
                                  key={index}
                                  variant={shouldWarnOnExisting ? 'destructive' : 'secondary'}
                                  className="text-xs"
                                >
                                  {sku}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}
